// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { useLoginState, useFeatures, useViewerData } from '@xing-com/hub';
import {
  IconJobs,
  IconJobsFilled,
  IconCompanies,
  IconCompaniesFilled,
  IconContacts,
  IconContactsFilled,
  IconOtherServices,
  IconNews,
  IconNewsFilled,
  IconNotificationCenter,
  IconNotificationFilled,
  IconBookmark,
  IconBookmarkFilled,
  IconMessages,
  IconMessagesFilled,
  IconSearch,
} from '@xing-com/icons';
import { Section } from '@xing-com/layout-primitives';
import { ProfileImage } from '@xing-com/profile-image';
import { ProgressBadge } from '@xing-com/progress-badge';

import { ActivityCenterButton } from './activity-center-button/activity-center-button';
import {
  StyledActionsContainer,
  StyledHeader,
  StyledHeaderPlacebo,
  StyledHeaderWrapper,
  StyledLogo,
  StyledLogoWrapper,
  StyledProfileImageWrapper,
  StyledButterfly,
  StyledOnlyOnBigScreenContainer,
} from './header.styles';
import { LoginButton } from './login-button/login-button';
import { NavItemHeader } from './nav-item-header';
import { NotificationCenter } from './notification-center';
import { ProductsMenu } from './products-menu/products-menu';
import { ProductsMenuItem } from './products-menu/products-menu-item';
import { StyledProductsMenuWrapper } from './products-menu/products-menu.styles';
import { SearchDropdown } from './search-dropdown';
import { Boneyard } from './upsell-boneyard/boneyard';

type LogoProps = {
  homeLink?: string;
};

const HeaderLogo: FC<LogoProps> = ({ homeLink }) => {
  return (
    <StyledLogoWrapper>
      <StyledLogo homeLink={homeLink} dataQa="logo" />
      <StyledButterfly homeLink={homeLink} dataQa="logoMobile" />
    </StyledLogoWrapper>
  );
};

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

export type HeaderProps = {
  className?: string;
  homeLink?: string;
  loginLink?: string;
  loginTracking?: { sc_o: string };
};

export const Header: React.FC<HeaderProps> = ({
  className,
  homeLink,
  loginLink,
}) => {
  const isLoggedOut = useLoginState() === 'LOGGED_OUT';
  const { showUpsellHint, showJobSeekerBoneyardUpsellHint } =
    useFeatures()?.data || {};

  const { formatMessage } = useIntl();
  const viewerData = useViewerData();
  const profileImageUrl = (viewerData?.data?.user?.profileImage ?? [])[0]?.url;
  const networkEfficiencyIndicator =
    viewerData?.data?.networkEfficiencyIndicator || {};

  const { score, userTypeSupported } = networkEfficiencyIndicator;
  const showProfileFillingIndicator = userTypeSupported && score && score !== 1;

  return (
    <StyledHeaderPlacebo isLoggedOut={isLoggedOut} className={className}>
      <StyledHeaderWrapper isLoggedOut={isLoggedOut}>
        <Section noBlockPadding>
          <StyledHeader isLoggedOut={isLoggedOut}>
            <HeaderLogo homeLink={homeLink} />
            {/* ###### Logout Area ###### */}
            <StyledActionsContainer data-testid="header-navigation">
              {isLoggedOut ? (
                <>
                  <StyledOnlyOnBigScreenContainer>
                    <NavItemHeader
                      activeItemValues={['jobs']}
                      icon={IconJobs}
                      activeIcon={IconJobsFilled}
                      title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
                      href="https://www.xing.com/jobs"
                      tracking={{
                        sc_o: 'navigation_find_jobs',
                      }}
                    />

                    <NavItemHeader
                      activeItemValues={['companies']}
                      icon={IconCompanies}
                      activeIcon={IconCompaniesFilled}
                      title={formatMessage({ id: 'MAINNAV_COMPANIES' })}
                      href="https://www.xing.com/companies"
                      tracking={{ sc_o: 'navigation_companies' }}
                    />
                    <NavItemHeader
                      icon={IconContacts}
                      activeIcon={IconContactsFilled}
                      title={formatMessage({ id: 'MAINNAV_NETWORK' })}
                      href="https://www.xing.com/network"
                      activeItemValues={['members']}
                      tracking={{
                        sc_o: 'navigation_network',
                      }}
                    />
                    <NavItemHeader
                      icon={IconNews}
                      activeIcon={IconNewsFilled}
                      title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
                      href="https://www.xing.com/insights"
                      activeItemValues={['insights']}
                      tracking={{
                        sc_o: 'navigation_insights',
                      }}
                    />
                  </StyledOnlyOnBigScreenContainer>
                  <ActivityCenterButton
                    item={{
                      key: 'activityCenter',
                      href: 'https://xing.com/activity?sc_o=navigation_activity',
                    }}
                  />

                  <LoginButton loginLink={loginLink} />
                  <StyledOnlyOnBigScreenContainer>
                    <ProductsMenu
                      item={{
                        key: 'business',
                        icon: IconOtherServices,
                        title: formatMessage({
                          id: 'MAINNAV_PRODUCTS',
                        }),
                        href: 'https://www.xing.com/business-solutions',
                      }}
                    >
                      <ProductsMenuItem
                        link={{
                          href: 'https://recruiting.xing.com',
                          title: formatMessage({
                            id: 'MAINNAV_BUSINESS_SOLUTIONS_RECRUITING',
                          }),
                          tracking: {
                            sc_o: 'navigation_business_solutions_recruiting',
                          },
                        }}
                      />
                      <ProductsMenuItem
                        link={{
                          href: 'https://werben.xing.com',
                          title: formatMessage({
                            id: 'MAINNAV_BUSINESS_SOLUTIONS_ADVERTISING',
                          }),
                          tracking: {
                            sc_o: 'navigation_business_solution_advertise',
                          },
                        }}
                      />
                    </ProductsMenu>
                  </StyledOnlyOnBigScreenContainer>
                </>
              ) : (
                // ###### Login Area ######
                <>
                  <NavItemHeader
                    activeItemValues={['jobs']}
                    icon={IconJobs}
                    activeIcon={IconJobsFilled}
                    title={formatMessage({ id: 'MAINNAV_JOBS_FIND' })}
                    href="/jobs/find"
                    tracking={{ sc_o: 'navigation_find_jobs' }}
                  />
                  <NavItemHeader
                    activeItemValues={['my-jobs']}
                    icon={IconBookmark}
                    activeIcon={IconBookmarkFilled}
                    title={formatMessage({ id: 'MAINNAV_MYJOBS' })}
                    href="/jobs/my-jobs"
                    tracking={{
                      sc_o: 'navigation_your_jobs',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_jobs',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  <NavItemHeader
                    icon={IconContacts}
                    activeIcon={IconContactsFilled}
                    title={formatMessage({ id: 'MAINNAV_NETWORK' })}
                    href="/network"
                    activeItemValues={['members']}
                    tracking={{
                      sc_o: 'navigation_network',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_network',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  <NavItemHeader
                    icon={IconNews}
                    activeIcon={IconNewsFilled}
                    title={formatMessage({ id: 'MAINNAV_INSIGHTS' })}
                    href="/discover/updates"
                    activeItemValues={['disco']}
                    tracking={{
                      sc_o: 'navigation_insights',
                      sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                    }}
                  />
                  <NavItemHeader
                    icon={IconMessages}
                    activeIcon={IconMessagesFilled}
                    title={formatMessage({ id: 'MAINNAV_MESSAGES' })}
                    href="/chats"
                    activeItemValues={['messages']}
                    notificationKey="unreadChats"
                    tracking={{
                      sc_o: 'navigation_messages',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_messages',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  <NotificationCenter
                    activeItemKey="supi"
                    notificationKey="ncWeb"
                    icon={IconNotificationCenter}
                    activeIcon={IconNotificationFilled}
                    href="/notifications"
                    title={formatMessage({ id: 'NOTIFICATION_CENTER_LINK' })}
                    tracking={{
                      sc_o: 'navigation_notifications',
                      sc_o_PropActionOrigin: [
                        'navigation_badge_notifications',
                        NAVIGATION_BADGE_NO_BADGE,
                      ],
                    }}
                  />
                  <SearchDropdown
                    icon={IconSearch}
                    title={formatMessage({ id: 'FRAME_HEADER_SEARCH' })}
                  ></SearchDropdown>
                  <StyledProfileImageWrapper>
                    <ProfileImage
                      data-testid="header-profile-logo"
                      src={profileImageUrl}
                      size="small"
                      // data-testid="header-profile-logo"
                      progressBadge={
                        showProfileFillingIndicator ? (
                          <ProgressBadge
                            progress={Math.round(score * 100)}
                            data-testid="top-bar-profile-progress-badge"
                          />
                        ) : null
                      }
                    />
                  </StyledProfileImageWrapper>
                  {!showUpsellHint ? null : showJobSeekerBoneyardUpsellHint ? (
                    <Boneyard
                      testId="upsell-button"
                      size="small"
                      variant="proJobs"
                      href="/purchase/projobs?reagent=uplt_277&sc_o=navigation_projobs_cta"
                    >
                      {formatMessage({
                        id: 'FRAME_HEADER_UPSELL_PROJOBS',
                      })}
                    </Boneyard>
                  ) : (
                    <Boneyard
                      testId="upsell-button"
                      size="small"
                      variant="premium"
                      href="/purchase/premium?reagent=uplt_95&sc_o=navigation_premium_cta"
                    >
                      {formatMessage({
                        id: 'FRAME_HEADER_UPSELL_PREMIUM',
                      })}
                    </Boneyard>
                  )}

                  <StyledProductsMenuWrapper>
                    <ProductsMenu
                      item={{
                        key: 'business',
                        icon: IconOtherServices,
                        title: formatMessage({
                          id: 'MAINNAV_PRODUCTS',
                        }),
                        // left link in there, but will eventually be changed in the future
                        href: 'https://www.xing.com/business-solutions',
                      }}
                    >
                      <ProductsMenuItem
                        link={{
                          href: 'https://recruiting.xing.com',
                          title: formatMessage({
                            id: 'MAINNAV_BUSINESS_SOLUTIONS_RECRUITING',
                          }),
                          tracking: {
                            sc_o: 'navigation_business_solutions_recruiting',
                          },
                        }}
                      />
                      <ProductsMenuItem
                        link={{
                          href: 'https://werben.xing.com',
                          title: formatMessage({
                            id: 'MAINNAV_BUSINESS_SOLUTIONS_ADVERTISING',
                          }),
                          tracking: {
                            sc_o: 'navigation_business_solution_advertise',
                          },
                        }}
                      />
                    </ProductsMenu>
                  </StyledProductsMenuWrapper>
                </>
              )}
            </StyledActionsContainer>
          </StyledHeader>
        </Section>
      </StyledHeaderWrapper>
    </StyledHeaderPlacebo>
  );
};
